import React, {useCallback, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import i18n from '../../utils/i18n';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import StatusRippleHeader from '@adsk/bim360-matrix-react-status-ripple-header';
import Text from '@adsk/bim360-matrix-react-text';
import {issueAttributesType, statusOptionsArrayType} from '../../constants/proptype-validation';
import {statusColorMapping} from '../../constants/status-types';
import {viewTypes} from '../../constants/view-types';
import {getSingleReportDataObject} from '../../utils/reportHelpers';
import exportIcon from '../../assets/images/icons/icon-export-thin.svg';
import {toReportFileNameDate} from '../../utils/date-helpers';
import {trimFileName} from '../../utils/notificationHelpers';
import TryAgainErrorNotification from '../common/TryAgainErrorNotification';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {hasPermittedAttribute, getLinkedDocument} from '../../utils/issues-helpers';
import useCurrentIssuePreviewPopup, {IssuePreviewPopupTypes} from './useCurrentIssuePreviewPopup';
import {
  getCurrentPreviewIssueIsUpdating,
  getCurrentViewType,
  getReportContainerId,
  isCreatingReport
} from '../../app/selectors';
import {createReportSyncAction, getModelTypeAndUnit} from '../../app/actions';
import {connect} from '../../app/connect';
import cx from 'classnames';
import BcfModal from '../common/BcfModal';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {isSupportedUnitAndType} from '../../utils/bcf-helpers';
import {isFedrampRegion} from '../../utils/region-helpers';

const useForceUpdateSubtree = () => {
  const [key, setKey] = useState(0);
  const forceUpdate = () => setKey(k => k + 1);
  return [key.toString(), forceUpdate];
};

const IssuePreviewHeader = ({
  onStatusChange,
  issue,
  issueStatusOptions,
  changeToStatus,
  enableStatusChange,
  isPDFReportEnabled,
  isCreatingPdfReport,
  dispatch,
  container,
  viewType,
  isUpdating
}) => {
  const [key, forceUpdate] = useForceUpdateSubtree();
  const [showBcfModal, setShowBcfModal] = useState(false);

  const createReport = useCallback(() => {
    const {title, [ISSUE_ATTRIBUTE_NAMES.ID]: displayId} = issue;

    const reportDataObject = getSingleReportDataObject(issue);
    const currentDate = toReportFileNameDate(new Date());
    const fileName = `${displayId}_${title}_${currentDate}.pdf`;
    const trimmedFileName = trimFileName(fileName, 35);
    const textExportFailed = i18n.t('notifications.failedToDownload', {
      fileName: trimmedFileName
    });
    const failureMessage = <TryAgainErrorNotification message={textExportFailed} tryAgainAction={createReport} />;
    const asyncMessage = (
      <Text hasHTML>{i18n.t('notifications.preparingToDownload', {fileName: trimmedFileName})}</Text>
    );

    dispatch(createReportSyncAction(reportDataObject, issue, fileName, asyncMessage, failureMessage));
  }, [issue, dispatch]);
  const closeModal = () => {
    setShowBcfModal(false);
  };

  const exportBcfModal = () => {
    setShowBcfModal(true);
  };
  const exportButtonConfig = useMemo(() => {
    const exportTooltipText = i18n.t('export.exportSinglePDFButtonTooltip');
    let config = null;
    const shouldShowExport = viewType === viewTypes.PROJECT && (isPDFReportEnabled || isFedrampRegion());

    if (shouldShowExport) {
      config = {
        icon: exportIcon,
        onClick: createReport,
        tooltipText: exportTooltipText
      };

      // Disable export button when exporting in progress
      if (isCreatingPdfReport) {
        config.disabled = true;
        config.tooltipText = null;
      }
    }

    return config;
  }, [createReport, isCreatingPdfReport, isPDFReportEnabled, viewType]);

  const actionsMenuConfig = useMemo(() => {
    const showExport = viewType !== viewTypes.PROJECT && (isPDFReportEnabled || isFedrampRegion());
    const exportOptions = [];
    const {modelUnit, modelType} = dispatch(getModelTypeAndUnit());
    const showBcfExport =
      featureFlags.get(FEATURE_FLAGS.BIM360_BCF_REPORT) &&
      getLinkedDocument(issue)?.details &&
      isSupportedUnitAndType(modelUnit, modelType) &&
      !isFedrampRegion();
    if (showExport || showBcfExport) {
      if (showExport) {
        exportOptions.push({
          key: 'EXPORT',
          label: i18n.t('export.exportSinglePDFButtonTooltip'),
          // As we don't have a SVGStore, we need to pass children here so it'll be passed down
          // to SvgIcon (rendered by ActionButton in the StatusRippleHeader) and it'll ignore the svgId
          svg: exportIcon,
          width: '20px',
          height: '20px',
          disabled: isCreatingPdfReport
        });
      }
      if (showBcfExport) {
        exportOptions.push({
          key: 'EXPORTBCF',
          label: i18n.t('export.exportAsBCF'),
          svg: exportIcon,
          width: '20px',
          height: '20px'
        });
      }
      return {
        options: exportOptions,
        container,
        onSelect: eventName => (eventName === 'EXPORTBCF' ? exportBcfModal() : createReport())
      };
    }

    return null;
  }, [container, createReport, isCreatingPdfReport, isPDFReportEnabled, viewType, issue, dispatch]);

  const statusOptionRenderer = useCallback((value, label) => {
    const option = issueStatusOptions.find(s => s.value === value);
    return (
      <StatusColorIndicatorOption
        statusColor={statusColorMapping[option.value]}
        label={label}
        subText={option.description}
      />
    );
  }, [issueStatusOptions]);

  const currentStatus = changeToStatus ? changeToStatus.value : issue.status;
  const currentStatusOption = issueStatusOptions.find(status => status.value === currentStatus);
  const {setCurrentIssuePreviewPopup} = useCurrentIssuePreviewPopup();
  // When issue have no type and user can change status field, we want to show warning for the user.
  const isIssueWithoutType =
    isEmpty(issue.permittedStatuses) &&
    hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.STATUS) &&
    !issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID];
  return (
    <div
      className={cx('IssuePreviewHeader', {
        'IssuePreviewHeader--updating': isUpdating
      })}
      data-testid="IssuePreviewHeader"
    >
      {/*The key needs to change whenever the Issue's type changes to prevent a crash
        caused by a bug in SelectDropdown getting fewer options on a subsequent render.*/}
      <StatusRippleHeader
        key={`${key}-${issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]}`}
        statusSelectDropdownProps={{
          'data-testid': 'IssuePreviewHeader__status',
          disabled: isUpdating,
          ...(isIssueWithoutType
            ? {
              onOpen: () => {
                setCurrentIssuePreviewPopup(IssuePreviewPopupTypes.NO_TYPE_STATUS_CHANGE);
                forceUpdate();
              }
            }
            : {})
        }}
        onStatusChange={onStatusChange}
        optionRenderer={statusOptionRenderer}
        enableStatusChange={enableStatusChange}
        superTitle={i18n.t(`issueType.${TYPE_QUALITY_ISSUES}.label_capitalized`)}
        title={`#${issue[ISSUE_ATTRIBUTE_NAMES.ID]}`}
        changeToStatus={changeToStatus}
        currentStatusOption={currentStatusOption}
        currentStatusName={currentStatusOption.label}
        currentStatusColorClassName={currentStatusOption.colorClassName}
        // StatusRippleHeader would show status dropdown only if more than one option exists.
        // When isIssueWithoutType is true - we want to show dropdown so we increase the options array length.
        statusOptions={isIssueWithoutType && issueStatusOptions.length === 1
          ? [issueStatusOptions[0], issueStatusOptions[0]]
          : issueStatusOptions}
        extraActionButton={exportButtonConfig}
        actionsMenuConfig={actionsMenuConfig}
      />
      {showBcfModal && <BcfModal container={container} closeModal={closeModal} issue={issue} />}
    </div>
  );
};

IssuePreviewHeader.displayName = 'IssuePreviewHeader';

IssuePreviewHeader.propTypes = {
  onStatusChange: PropTypes.func.isRequired,
  issue: issueAttributesType,
  issueStatusOptions: statusOptionsArrayType.isRequired,
  changeToStatus: PropTypes.object,
  enableStatusChange: PropTypes.bool.isRequired,
  isPDFReportEnabled: PropTypes.bool.isRequired,
  isCreatingPdfReport: PropTypes.bool,
  createReportSync: PropTypes.func.isRequired,
  container: PropTypes.object.isRequired,
  viewType: PropTypes.string,
  isUpdating: PropTypes.bool,
  dispatch: PropTypes.func.isRequired
};

export default connect(state => ({
  isPDFReportEnabled: !!getReportContainerId(state),
  isCreatingPdfReport: isCreatingReport(state),
  viewType: getCurrentViewType(state),
  isUpdating: getCurrentPreviewIssueIsUpdating(state)
}))(IssuePreviewHeader);
